// selectors for reducers
import { createSelector } from '@reduxjs/toolkit';

import UILocale from '../translations/UILocale';


export const getActiveListingPage = createSelector(
  (state) => state.listingPage.pages.find((page) => page.isActive),
  (activeListingPage) => activeListingPage
);

export const getNumberOfListingPagesStoredInRedux = createSelector(
  (state) => state.listingPage.pages.length,
  (numberOfListingPagesStoredInRedux) => numberOfListingPagesStoredInRedux
);

export const getActiveListingPageFiltersFromSearchLabs = createSelector(
  getActiveListingPage,
  (activeListingPage) => activeListingPage.filtersFromSearchLabs
);

export const getActiveListingPageProductIds = createSelector(
  getActiveListingPage,
  (activeListingPage) => activeListingPage.productList.map((product) => product.id)
);

export const getNumberOfActiveFiltersOnActiveListingPage = createSelector(
  getActiveListingPage,
  (activeListingPage) => {
    const isPriceFilterActive = typeof activeListingPage.pageAttributes.price_min !== 'undefined';
    let numberOfActiveFilters = isPriceFilterActive ? 1 : 0;

    activeListingPage.filtersFromSearchLabs.forEach(
      (filterItem) => {
        filterItem.values.forEach((valueItem) => {
          if (valueItem.selected) {
            numberOfActiveFilters++;
          }
        });
      }
    );

    // on search listing page, it can happen that
    // if even no filter selected is coming in the searchLabs response, a filter can be selected in reality
    // it probably happens because of a bug in searchlabs Search API
    // In this case, the following code corrects the number of selected filters by checking
    // activeListingPage.pageAttributes.filterAttributes which always contains the correct number of selected filters
    // for search listing page
    // TODO: as soon as the bug is corrected in searchLabs remove this code
    if (activeListingPage.searchListingPage &&
      numberOfActiveFilters === 0 &&
      typeof activeListingPage?.pageAttributes?.filterAttributes === 'object' &&
      Object.keys(activeListingPage.pageAttributes.filterAttributes).length > 0) {

      Object.keys(activeListingPage.pageAttributes.filterAttributes).forEach((filterTypeId) => {
        if (Array.isArray(activeListingPage.pageAttributes.filterAttributes[filterTypeId])) {
          numberOfActiveFilters += activeListingPage.pageAttributes.filterAttributes[filterTypeId].length;
        }
      });
    }

    return numberOfActiveFilters;
  }
);

export const getSelectedSingleBrandFilterLabelOnActiveListingPage = createSelector(
  getActiveListingPage,
  (activeListingPage) => {
    const selectedValuesOfBrandFilters =
      activeListingPage.filtersFromSearchLabs.find((filter) => filter.id === 'brand_id')
        ? activeListingPage.filtersFromSearchLabs.find((filter) => filter.id === 'brand_id')
          .values
          .filter((value) => value.selected)
        : undefined;

    if (selectedValuesOfBrandFilters && selectedValuesOfBrandFilters.length === 1) {
      return selectedValuesOfBrandFilters[0].label;
    }

    return null;
  }
);

export const getSelectedSingleBrandFilterValueOnActiveListingPage = createSelector(
  getActiveListingPage,
  (activeListingPage) => {
    const selectedValuesOfBrandFilters =
      activeListingPage.filtersFromSearchLabs.find((filter) => filter.id === 'brand_id')
        ? activeListingPage.filtersFromSearchLabs.find((filter) => filter.id === 'brand_id')
          .values
          .filter((value) => value.selected)
        : undefined;

    if (selectedValuesOfBrandFilters && selectedValuesOfBrandFilters.length === 1) {
      return selectedValuesOfBrandFilters[0].value;
    }

    return null;
  }
);

export const getSelectedSingleTypeFilterLabelOnActiveListingPage = createSelector(
  getActiveListingPage,
  (activeListingPage) => {
    const selectedValuesOfTypeFilters =
      activeListingPage.filtersFromSearchLabs.find((filter) => filter.label === 'Type')
        ? activeListingPage.filtersFromSearchLabs.find((filter) => filter.label === 'Type')
          .values
          .filter((value) => value.selected)
        : undefined;

    if (selectedValuesOfTypeFilters && selectedValuesOfTypeFilters.length === 1) {
      return selectedValuesOfTypeFilters[0].label;
    }

    return null;
  }
);

export const getSelectedSingleTypeFilterValueOnActiveListingPage = createSelector(
  getActiveListingPage,
  (activeListingPage) => {
    const selectedValuesOfTypeFilters =
      activeListingPage.filtersFromSearchLabs.find((filter) => filter.label === 'Type')
        ? activeListingPage.filtersFromSearchLabs.find((filter) => filter.label === 'Type')
          .values
          .filter((value) => value.selected)
        : undefined;

    if (selectedValuesOfTypeFilters && selectedValuesOfTypeFilters.length === 1) {
      return selectedValuesOfTypeFilters[0].value;
    }

    return null;
  }
);

export const getSelectedSingleSegmentFilterLabelOnActiveListingPage = createSelector(
  getActiveListingPage,
  (activeListingPage) => {
    const selectedValuesOfSegmentFilters =
      activeListingPage.filtersFromSearchLabs.find((filter) => filter.id === 'segment_id')
        ? activeListingPage.filtersFromSearchLabs.find((filter) => filter.id === 'segment_id')
          .values
          .filter((value) => value.selected)
        : undefined;

    if (selectedValuesOfSegmentFilters && selectedValuesOfSegmentFilters.length === 1) {
      return selectedValuesOfSegmentFilters[0].label;
    }

    return null;
  }
);

export const getPageHeaderTitleForActiveSegmentListingPage = createSelector(
  getActiveListingPage,
  getSelectedSingleBrandFilterLabelOnActiveListingPage,
  getSelectedSingleTypeFilterLabelOnActiveListingPage,
  (activeListingPage, selectedSingleBrandFilterLabel, selectedSingleTypeFilterLabel) => {
    const segmentName = activeListingPage.ucmsCategorization.segmentName;
    
    // if one brand filter is selected and one type filter is selected we show brand label + type label
    if (selectedSingleBrandFilterLabel && selectedSingleTypeFilterLabel) {
      return `${selectedSingleBrandFilterLabel} ${selectedSingleTypeFilterLabel}`;
    }

    // if one brand filter is selected we show brand label + segment title in page title
    if (selectedSingleBrandFilterLabel) {
      return `${selectedSingleBrandFilterLabel} ${segmentName}`;
    }

    // if one type filter is selected we show type filter label
    if (selectedSingleTypeFilterLabel) {
      return selectedSingleTypeFilterLabel;
    }

    // in all the other cases just name of segment is shown
    return segmentName;
  }
);

export const getPageHeaderTitleForActiveSegmentListingPageWithoutBrand = createSelector(
  getActiveListingPage,
  getSelectedSingleBrandFilterLabelOnActiveListingPage,
  getSelectedSingleTypeFilterLabelOnActiveListingPage,
  (activeListingPage, selectedSingleBrandFilterLabel, selectedSingleTypeFilterLabel) => {
    const segmentName = activeListingPage.ucmsCategorization.segmentName;
    
    // if one brand filter is selected and one type filter is selected we show brand label + type label
    if (selectedSingleBrandFilterLabel && selectedSingleTypeFilterLabel) {
      return `${selectedSingleBrandFilterLabel} ${selectedSingleTypeFilterLabel}`;
    }

    // if one brand filter is selected we show brand label + segment title in page title
    if (selectedSingleBrandFilterLabel) {
      return `${selectedSingleBrandFilterLabel} ${segmentName}`;
    }

    // if one type filter is selected we show type filter label
    if (selectedSingleTypeFilterLabel) {
      return selectedSingleTypeFilterLabel;
    }

    // in all the other cases just name of segment is shown
    return segmentName;
  }
);

export const getPageHeaderTitleForForSearchActiveListingPage = createSelector(
  getActiveListingPage,
  getNumberOfActiveFiltersOnActiveListingPage,
  getSelectedSingleSegmentFilterLabelOnActiveListingPage,
  (activeListingPage, numberOfActiveFilters, selectedSingleSegmentFilterLabel) => {
    const searchQuery = activeListingPage.pageAttributes.query;

    // if search page has no query no header title is shown
    if (!searchQuery) {
      return '';
    }

    const segmentLabelAfterPriceChange = activeListingPage.pageAttributes.segmentFilter
      ? activeListingPage.pageAttributes.segmentFilter.label
      : undefined;

    if (activeListingPage.productList.length === 0 && numberOfActiveFilters === 0) {
      // search listing page with no result
      return `We can't find a match for "${searchQuery}"`; // TODO: add to translations
    }

    // search listing page with result
    return `${UILocale.getString('component.searchproductlisting.showingResultsFor')} "${searchQuery}"` +
      `${(selectedSingleSegmentFilterLabel || segmentLabelAfterPriceChange)
        ? ` in ${selectedSingleSegmentFilterLabel || segmentLabelAfterPriceChange}`
        : ''}`;
  }
);

export const getTitleHTMLElementValue = createSelector(
  getActiveListingPage,
  getSelectedSingleBrandFilterLabelOnActiveListingPage,
  getSelectedSingleTypeFilterLabelOnActiveListingPage,
  (activeListingPage, selectedSingleBrandFilterLabel, selectedSingleTypeFilterLabel) => {
    const segmentName = activeListingPage.ucmsCategorization ? activeListingPage.ucmsCategorization.segmentName : '';

    return UILocale.getFormattedString('component.productlisting.title', {
      s: selectedSingleTypeFilterLabel ? '' : segmentName, // if selectedSingleTypeFilterLabel exists, name of segment is not used in <title>
      brand: selectedSingleBrandFilterLabel ? selectedSingleBrandFilterLabel : '',
      attribute: selectedSingleTypeFilterLabel ? selectedSingleTypeFilterLabel : ''
    });
  }
);

export const getSEOConfiguration = createSelector(
  (state) => state.listingPage.staticData.seoConfiguration,
  (seoConfiguration) => seoConfiguration
);

export const getSEOInformationForSegmentListingPageServerRendering = createSelector(
  getTitleHTMLElementValue,
  getPageHeaderTitleForActiveSegmentListingPage,
  getSEOConfiguration,
  getSelectedSingleBrandFilterValueOnActiveListingPage,
  getSelectedSingleTypeFilterValueOnActiveListingPage,
  getNumberOfActiveFiltersOnActiveListingPage,
  getActiveListingPage,
  (titleHTMLElementValue, pageHeaderTitle, seoConfiguration, selectedSingleBrandFilterValue,
    selectedSingleTypeFilterValue, numberOfActiveFiltersOnActiveListingPage, activeListingPage) => {
    let robots;
    let title;
    let description;
    let keywords;

    const defaultHardcodedRobots = 'noindex, follow';
    const defaultHardcodedTitle = titleHTMLElementValue;
    const defaultHardcodedDescription = UILocale.getFormattedString('metatag.description.hardcoded', { pageHeaderTitle });
    const defaultHardcodedKeywords = UILocale.getFormattedString('metatag.keywords.hardcoded', { pageHeaderTitle });


    if (selectedSingleBrandFilterValue && selectedSingleTypeFilterValue && numberOfActiveFiltersOnActiveListingPage === 2) {
      // if one brand filter is selected and one type filter is selected
      const seoConfigurationBasedOnFilters = seoConfiguration?.filtersSelectedOptions?.find((configurationItem) => (
        configurationItem.selectedSingleBrandFilterValue === selectedSingleBrandFilterValue &&
        configurationItem.selectedSingleTypeFilterValue === selectedSingleTypeFilterValue
      ));

      robots = seoConfigurationBasedOnFilters?.robots ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.robots || defaultHardcodedRobots;
      title = seoConfigurationBasedOnFilters?.title ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.title || defaultHardcodedTitle;
      description = seoConfigurationBasedOnFilters?.description ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.description ||
        defaultHardcodedDescription;
      keywords = seoConfigurationBasedOnFilters?.keywords ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.keywords || defaultHardcodedKeywords;
    } else if (selectedSingleBrandFilterValue && numberOfActiveFiltersOnActiveListingPage === 1) {
      // if one brand filter is selected
      const seoConfigurationBasedOnBrandFilter = seoConfiguration?.filtersSelectedOptions?.find((configurationItem) => (
        configurationItem.selectedSingleBrandFilterValue === selectedSingleBrandFilterValue
      ));

      robots = seoConfigurationBasedOnBrandFilter?.robots ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.robots || defaultHardcodedRobots;
      title = seoConfigurationBasedOnBrandFilter?.title ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.title || defaultHardcodedTitle;
      description = seoConfigurationBasedOnBrandFilter?.description ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.description ||
        defaultHardcodedDescription;
      keywords = seoConfigurationBasedOnBrandFilter?.keywords ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.keywords || defaultHardcodedKeywords;
    } else if (selectedSingleTypeFilterValue && numberOfActiveFiltersOnActiveListingPage === 1) {
      // if one type filter is selected
      const seoConfigurationBasedOnTypeFilter = seoConfiguration?.filtersSelectedOptions?.find((configurationItem) => (
        configurationItem.selectedSingleTypeFilterValue === selectedSingleTypeFilterValue
      ));

      robots = seoConfigurationBasedOnTypeFilter?.robots ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.robots || defaultHardcodedRobots;
      title = seoConfigurationBasedOnTypeFilter?.title ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.title || defaultHardcodedTitle;
      description = seoConfigurationBasedOnTypeFilter?.description ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.description ||
        defaultHardcodedDescription;
      keywords = seoConfigurationBasedOnTypeFilter?.keywords ||
        seoConfiguration?.defaultSingleBrandOrTypeFilter?.keywords || defaultHardcodedKeywords;
    } else if (numberOfActiveFiltersOnActiveListingPage === 0) {
      // segment listing page without filters selected
      robots = seoConfiguration?.noFilterSelected?.robots || defaultHardcodedRobots;
      title = seoConfiguration?.noFilterSelected?.title || defaultHardcodedTitle;
      description = seoConfiguration?.noFilterSelected?.description || defaultHardcodedDescription;
      keywords = seoConfiguration?.noFilterSelected?.keywords || defaultHardcodedKeywords;

    } else {
      // all the other options
      robots = seoConfiguration?.defaultMultipleFilters?.robots || defaultHardcodedRobots;
      title = seoConfiguration?.defaultMultipleFilters?.title || defaultHardcodedTitle;
      description = seoConfiguration?.defaultMultipleFilters?.description || defaultHardcodedDescription;
      keywords = seoConfiguration?.defaultMultipleFilters?.keywords || defaultHardcodedKeywords;
    }

    // The listing page with some products cannot have 'noindex,nofollow' in robots,
    // It has to have 'noindex, follow' instead
    // In UBO segment settings, 'noindex,nofollow' is sometimes used for defaultMultipleFilters.robots
    // and this needs to be corrected
    // TODO: remove this code as soon as 'noindex, follow' is used everywhere in defaultMultipleFilters.robots in all segments
    //  settings in UBO
    if (numberOfActiveFiltersOnActiveListingPage !== 0 && robots.trim().replace(/\s/g, '') === 'noindex,nofollow') {
      robots = 'noindex, follow';
    }

    // Only first page, sorted by relevance with 20 products on the page can be indexed
    // All other cases have to be not indexed
    // eslint-disable-next-line camelcase
    if (activeListingPage?.pageAttributes?.page !== '1' || activeListingPage?.pageAttributes?.m_results_per_page !== '20' ||
      activeListingPage?.pageAttributes?.sort !== 'relevance-desc') {
      robots = 'noindex, follow';
    }

    return {
      robots,
      title,
      description,
      keywords
    };
  }
);

export const getPreviouslyResolvedLocations = createSelector(
  (state) => state.listingPage.locationSearchSetup.previouslyResolvedLocations,
  (previouslyResolvedLocations) => previouslyResolvedLocations
);

export const getActiveLocationName = createSelector(
  getActiveListingPage,
  getPreviouslyResolvedLocations,
  (activeListingPage, previouslyResolvedLocations) => (
    activeListingPage.productsDeliveriesSearch.locationId
      ? previouslyResolvedLocations.find((resolvedLocation) => resolvedLocation.id ===
      activeListingPage.productsDeliveriesSearch.locationId).location
      : null
  )
);

export const getCurrentSetLocationDescriptor = createSelector(
  (state) => state.listingPage.pages.find(
    (currentStoredPage) => currentStoredPage.productsDeliveriesSearch.locationId !== null
  ),
  getPreviouslyResolvedLocations,
  (storedPageWithLocation, previouslyResolvedLocations) => (
    storedPageWithLocation
      ? previouslyResolvedLocations.find(
        (resolvedLocation) => resolvedLocation.id === storedPageWithLocation.productsDeliveriesSearch.locationId
      )
      : null
  )
);
